import React, { useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import "../components/ChatArea.css";
import "../components/WelcomeState.css";
import {
    useAuth,
    useClerk,
    useUser,
} from "@clerk/clerk-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import PaywallModal from "../components/PaywallModal";
import optibotLogoNoText from "../girl-assistant3.png";
import { CheckSquare, Scale, FileText, Subtitles, Sparkles, ArrowLeft, Lock, ArrowRight } from "lucide-react";
import TopRightAuth from "../components/TopRightAuth";

/**
 * ChatPage — the main chat interface.
 *
 * Props passed from App:
 * - chat: the full return object from useChat hook
 * - showPaywall / setShowPaywall
 * - handleSubscribe / handleValidatePromo / isCheckoutLoading
 * - currentConversationId
 * - hasSubscription / trialExpired
 */
export default function ChatPage(props) {
    if (props.embedMode) {
        return (
            <ChatPageBody
                {...props}
                isSignedIn={true}
                userFirstName={null}
                inputBlocked={false}
                showAuthChrome={false}
                onRestrictedAction={(action) => { if (action) action(); }}
            />
        );
    }
    return <ChatPageWithClerk {...props} />;
}

function ChatPageWithClerk(props) {
    const { isSignedIn } = useAuth();
    const clerk = useClerk();
    const { user } = useUser();
    const {
        hasSubscription,
        trialExpired,
        setShowPaywall,
    } = props;

    const inputBlocked = !isSignedIn || (!hasSubscription && trialExpired);

    const onRestrictedAction = (action) => {
        if (!isSignedIn) {
            clerk.openSignIn();
            return;
        }
        if (!hasSubscription && trialExpired) {
            setShowPaywall(true);
            return;
        }
        if (action) action();
    };

    return (
        <ChatPageBody
            {...props}
            isSignedIn={isSignedIn}
            userFirstName={user?.firstName}
            inputBlocked={inputBlocked}
            showAuthChrome={true}
            onRestrictedAction={onRestrictedAction}
        />
    );
}

function ChatPageBody({
    chat,
    showPaywall,
    setShowPaywall,
    handleSubscribe,
    handleValidatePromo,
    isCheckoutLoading,
    currentConversationId,
    loadConversation,
    handleNewChat,
    embedMode = false,
    isSignedIn,
    userFirstName,
    inputBlocked,
    showAuthChrome,
    onRestrictedAction,
}) {
    const { conversationId: urlConversationId } = useParams();

    useEffect(() => {
        if (embedMode) return;
        if (urlConversationId && urlConversationId !== currentConversationId && loadConversation) {
            loadConversation(urlConversationId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlConversationId, embedMode]);

    const {
        messages, input, setInput, isTyping,
        copiedMessageIndex,
        isStructuredMode, setIsStructuredMode, connectionState,
        messagesEndRef,
        sendMessage, handleStopGenerating, handleRegenerate,
        copyToClipboard, renderStructuredBlock,
        ENABLE_STRUCTURED_MODE,
    } = chat;

    const textareaRef = useRef(null);
    const isWelcome = messages.length <= 1 && !currentConversationId;

    const autoResize = useCallback(() => {
        const el = textareaRef.current;
        if (!el) return;
        const maxHeight = 200;
        el.style.height = 'auto';
        const nextHeight = Math.min(el.scrollHeight, maxHeight);
        el.style.height = `${nextHeight}px`;
        el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
    }, []);

    useEffect(() => {
        autoResize();
    }, [input, autoResize]);

    const renderInputArea = () => (
        <div className={`input-wrapper ${isWelcome ? 'empty-state-input' : ''}`}>
            <div className="input-toolbar">
                <div className="connection-status">
                    {connectionState === "error" && "Connection issue. Retry."}
                </div>
                {ENABLE_STRUCTURED_MODE ? (
                    <button
                        className={`mode-toggle ${isStructuredMode ? "active" : ""}`}
                        onClick={() => setIsStructuredMode((prev) => !prev)}
                        type="button"
                    >
                        {isStructuredMode ? "Structured Mode: On" : "Structured Mode: Off"}
                    </button>
                ) : null}
            </div>
            {!isSignedIn ? (
                <button
                    type="button"
                    className="composer-cta-bar"
                    onClick={() => onRestrictedAction()}
                    aria-label="Sign in to ask a question"
                    title="Sign in to ask a question"
                >
                    <span className="composer-cta-bar-icon" aria-hidden="true">
                        <Lock size={18} />
                    </span>
                    <span className="composer-cta-bar-copy">
                        Sign in to ask about WCAG guidelines, Section 508 compliance...
                    </span>
                    <span className="composer-cta-bar-action" aria-hidden="true">
                        <span>Sign in</span>
                        <ArrowRight size={16} />
                    </span>
                </button>
            ) : inputBlocked ? (
                <button
                    type="button"
                    className="composer-cta-bar composer-cta-bar--upgrade"
                    onClick={() => onRestrictedAction()}
                    aria-label="Upgrade to continue chatting"
                    title="Upgrade to continue chatting"
                >
                    <span className="composer-cta-bar-icon" aria-hidden="true">
                        <Lock size={18} />
                    </span>
                    <span className="composer-cta-bar-copy">
                        Your free trial has ended. Upgrade to keep asking about WCAG & PDF/UA...
                    </span>
                    <span className="composer-cta-bar-action" aria-hidden="true">
                        <span>Upgrade</span>
                        <ArrowRight size={16} />
                    </span>
                </button>
            ) : (
            <div className="input-area">
                <label htmlFor="chat-input" className="sr-only">Ask a question</label>
                <textarea
                    id="chat-input"
                    ref={textareaRef}
                    className="chat-input"
                    aria-label="Ask a question"
                    placeholder="Ask about WCAG guidelines, Section 508 compliance..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            sendMessage();
                        }
                    }}
                    disabled={isTyping}
                    rows={1}
                    onInput={autoResize}
                />
                <button
                    className="send-button"
                    onClick={sendMessage}
                    disabled={isTyping || !input.trim()}
                    aria-label="Send message"
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></svg>
                </button>
                {isTyping && (
                    <button
                        className="send-button stop-button"
                        onClick={handleStopGenerating}
                        type="button"
                        title="Stop generating"
                        aria-label="Stop generating"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="6" width="12" height="12" rx="2" /></svg>
                    </button>
                )}
            </div>
            )}
        </div>
    );

    return (
        <React.Fragment>
            {/* Fade only while messages can scroll under the header buttons.
                On the welcome / sign-in screen this mask sat on top of the avatar. */}
            {!isWelcome && (
                <div className="chat-top-fade" aria-hidden="true" />
            )}
            {showAuthChrome && <TopRightAuth />}

            {!isWelcome && (
                <button
                    type="button"
                    onClick={handleNewChat}
                    className="chat-back-button fade-in-up"
                    title="Back to New Chat"
                    aria-label="Back to new chat"
                >
                    <ArrowLeft size={16} aria-hidden="true" focusable="false" />
                </button>
            )}

            <div className="main-content-container" style={{ display: 'flex' }}>
                {showAuthChrome && (
                    <PaywallModal
                        isVisible={showPaywall}
                        onClose={() => setShowPaywall(false)}
                        onSubscribe={handleSubscribe}
                        onValidatePromo={handleValidatePromo}
                        isCheckoutLoading={isCheckoutLoading}
                    />
                )}

                <div className="chat-container" style={{ animationDelay: '0.2s' }}>
                    <div className="sr-only" role="status" aria-live="polite">
                        {connectionState === "error" ? "Connection issue detected. Retry may be required." : ""}
                    </div>
                    {isWelcome ? (
                        <div className="welcome-state">
                            <div className="welcome-header">
                                <div className="welcome-logo-container">
                                    <img src={optibotLogoNoText} alt="OptiBot" className="welcome-logo" />
                                </div>
                                <h1 className="welcome-title">
                                    <span>Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}{userFirstName ? ` ${userFirstName}` : ''},</span>{' '}
                                    <span>I'm <span className="brand-primary-text">Accessibility Assistant</span></span>
                                </h1>
                                <p className="welcome-tagline">Expert guidance on WCAG & PDF/UA compliance.</p>
                            </div>

                            <p className="welcome-section-label">
                                <Sparkles aria-hidden="true" focusable="false" />
                                Try asking about...
                                <Sparkles aria-hidden="true" focusable="false" />
                            </p>

                            <div className="prompt-chips">
                                <button
                                    className="prompt-chip"
                                    onClick={() =>
                                        onRestrictedAction(() => setInput("What are the key requirements to achieve WCAG 2.1 Level AA compliance and above?"))
                                    }
                                    aria-label="WCAG 2.1 Level AA & Above"
                                    title="Click to ask OptiBot about this topic"
                                >
                                    <CheckSquare className="prompt-chip-icon" aria-hidden="true" focusable="false" />
                                    <span>WCAG 2.1 Level AA & Above</span>
                                </button>
                                <button
                                    className="prompt-chip"
                                    onClick={() =>
                                        onRestrictedAction(() => setInput("What legislation governs digital accessibility and what are the legal ramifications of non-compliance?"))
                                    }
                                    aria-label="Legislation & Legal Ramifications"
                                    title="Click to ask OptiBot about this topic"
                                >
                                    <Scale className="prompt-chip-icon" aria-hidden="true" focusable="false" />
                                    <span>Legislation & Legal Ramifications</span>
                                </button>
                                <button
                                    className="prompt-chip"
                                    onClick={() =>
                                        onRestrictedAction(() => setInput("What are the best practices for creating accessible documents and web content, and how do I remediate existing ones?"))
                                    }
                                    aria-label="Document & Web Remediation"
                                    title="Click to ask OptiBot about this topic"
                                >
                                    <FileText className="prompt-chip-icon" aria-hidden="true" focusable="false" />
                                    <span>Document & Web Remediation</span>
                                </button>
                                <button
                                    className="prompt-chip"
                                    onClick={() =>
                                        onRestrictedAction(() => setInput("What are the requirements and best practices for adding captions to video and multimedia content for accessibility?"))
                                    }
                                    aria-label="Captioning"
                                    title="Click to ask OptiBot about this topic"
                                >
                                    <Subtitles className="prompt-chip-icon" aria-hidden="true" focusable="false" />
                                    <span>Captioning</span>
                                </button>
                            </div>

                            <div className="welcome-or-divider">
                                <p className="welcome-or-label">
                                    OR
                                </p>
                                {renderInputArea()}
                            </div>
                        </div>
                    ) : (
                        <div className="chat-history" role="log" aria-live="polite" aria-relevant="additions text" aria-label="Conversation messages">
                            {messages.map((msg, idx) => {
                                // Skip the initial greeting message if it's the exact same to keep UI clean
                                if (idx === 0 && msg.sender === 'bot' && msg.content.includes("Hello! I can help you with digital accessibility compliance")) return null;
                                return (
                                    <div
                                        key={idx}
                                        className={`message-row ${msg.sender === "user" ? "user" : "bot"} fade-in-up`}
                                    >
                                        {msg.sender !== "user" && (
                                            <div className="bot-avatar-container">
                                                <img src={optibotLogoNoText} alt="Bot" className="bot-avatar" />
                                            </div>
                                        )}
                                        <div className="message-container">
                                            <div
                                                className={
                                                    msg.sender === "user" ? "user-message" : "bot-message"
                                                }
                                            >
                                                {msg.sender === "bot" || msg.sender === "bot_typing" ? (
                                                    <>
                                                        {msg.structuredData ? renderStructuredBlock(msg.structuredData) : null}
                                                        <div className="bot-markdown">
                                                            <ReactMarkdown
                                                                remarkPlugins={[remarkGfm]}
                                                                components={{
                                                                    a: ({ node, ...props }) => (
                                                                        <a
                                                                            {...props}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                        >
                                                                            {props.children}
                                                                        </a>
                                                                    ),
                                                                }}
                                                            >
                                                                {msg.content}
                                                            </ReactMarkdown>
                                                        </div>
                                                        {Array.isArray(msg.sources) && msg.sources.length > 0 ? (
                                                            <div className="sources-list">
                                                                <div className="sources-title">Sources</div>
                                                                {msg.sources.map((source, sourceIdx) => (
                                                                    <a
                                                                        key={`${source.url}-${sourceIdx}`}
                                                                        href={source.url}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="source-link"
                                                                    >
                                                                        {source.title || source.url}
                                                                    </a>
                                                                ))}
                                                            </div>
                                                        ) : null}
                                                    </>
                                                ) : msg.content}
                                            </div>
                                            {msg.sender === "bot" && (
                                                <div className="message-actions">
                                                    <button
                                                        className="copy-button"
                                                        data-copied={copiedMessageIndex === idx}
                                                        onClick={() => copyToClipboard(msg.content, idx)}
                                                        title="Copy response"
                                                        aria-label="Copy response"
                                                    >
                                                        {copiedMessageIndex === idx ? (
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                                                        ) : (
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect width="14" height="14" x="8" y="8" rx="2" ry="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" /></svg>
                                                        )}
                                                    </button>
                                                    <button
                                                        className="copy-button"
                                                        onClick={handleRegenerate}
                                                        title="Regenerate response"
                                                        aria-label="Regenerate response"
                                                    >
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M23 4v6h-6" /><path d="M1 20v-6h6" /><path d="M3.51 9a9 9 0 0 1 14.13-3.36L23 10" /><path d="M20.49 15A9 9 0 0 1 6.36 18.36L1 14" /></svg>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            {isTyping && (
                                <div className="message-row bot fade-in-up">
                                    <div className="bot-avatar-container">
                                        <img src={optibotLogoNoText} alt="Bot" className="bot-avatar" />
                                    </div>
                                    <div className="message-container">
                                        <div className="typing-indicator" style={{ display: 'flex', alignItems: 'center' }} role="status" aria-live="polite">
                                            <span style={{ fontSize: '14px', color: 'var(--color-text-muted)', fontStyle: 'italic', marginRight: '24px' }}>Thinking...</span>
                                            <div className="dot-pulse"></div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>
                    )}

                    {(messages.length > 1 || currentConversationId) && renderInputArea()}
                </div>
            </div>
        </React.Fragment>
    );
}
